import { parseAsInteger, parseAsNumberLiteral, useQueryState } from 'nuqs'
import { useMemo } from 'react'
import { PAGE_LIMIT_OPTIONS } from '~/components/Pagination'

/** The middle option. Enough rows to scan a list of rules or contexts without reaching for page 2 immediately. */
const DEFAULT_LIMIT = 25

/**
 * Paginate a list the browser already holds in full.
 *
 * For the lists whose endpoint answers with everything — rules, contexts, context keys. They are small
 * enough that a server-side page would be a request per scroll for no gain, but long enough that a
 * hundred rows in one scroll is not a list anyone reads. The rows are sliced here; the endpoint is
 * untouched.
 *
 * Page and limit live in the URL, like every other paginated list in the app, so a link to page 3 opens
 * on page 3 and browser back steps through pages rather than out of the list.
 *
 * The current page is **clamped rather than corrected**: filtering a list down to fewer pages than the
 * one you are on must not write state during render, and clamping already shows the last real page. The
 * stale `?page=` in the URL settles itself the next time a page is picked, and means nothing until then.
 */
export const useClientPagination = <T>(items: T[], { defaultLimit = DEFAULT_LIMIT }: { defaultLimit?: number } = {}) => {
  const [page, setPage] = useQueryState<number>('page', parseAsInteger.withDefault(1).withOptions({ clearOnDefault: false }))
  const [limit, setLimit] = useQueryState<number>('limit', parseAsNumberLiteral(PAGE_LIMIT_OPTIONS).withDefault(defaultLimit).withOptions({ clearOnDefault: false }))

  const totalItems = items.length
  // At least one page, so an empty list still renders a pager reading "1 of 1" rather than "1 of 0".
  const totalPages = Math.max(1, Math.ceil(totalItems / limit))
  const currentPage = Math.min(Math.max(page, 1), totalPages)

  const pageItems = useMemo(() => items.slice((currentPage - 1) * limit, currentPage * limit), [items, currentPage, limit])

  return {
    pageItems,
    /** Spread straight onto `<Pagination />`. */
    paginationProps: {
      currentPage,
      totalPages,
      limit,
      totalItems,
      onPageChange: (next: number) => setPage(next),
      // Back to the first page: the row that was at the top of page 3 is not on page 3 of a longer page.
      onLimitChange: (next: number) => {
        setLimit(next)
        setPage(1)
      },
    },
    /** For a search box: a new query has no page 2 yet. */
    resetPage: () => setPage(1),
  }
}
